import { useEffect, useState, useCallback, useRef } from "react";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, mediaUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const KIND_LABEL = { feature: "Feature photos", banner: "Promo banners", logo: "Platform logo" };
const KIND_HINT = {
  feature: "Large showcase photos.",
  banner: "Slim promotional strips shown on the store page and inside the staff workspace.",
  logo: "Square image works best. Shown wherever the brand logo appears — uploading a new one replaces the old.",
};

export default function GalleryManager({ platform = false, kinds = ["feature", "banner"], testid = "gallery" }) {
  const [images, setImages] = useState([]);
  const [caption, setCaption] = useState({});
  const [busy, setBusy] = useState({});
  const fileRefs = useRef({});
  const headers = platform ? { "X-Tenant-Id": "" } : {};

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/gallery", { headers });
      setImages(data);
    } catch (e) { toast.error(fmtErr(e)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);
  useEffect(() => { load(); }, [load]);

  const upload = async (kind) => {
    const input = fileRefs.current[kind];
    const file = input?.files?.[0];
    if (!file) { toast.error("Choose an image file first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    if (caption[kind]?.trim()) fd.append("caption", caption[kind].trim());
    setBusy((b) => ({ ...b, [kind]: true }));
    try {
      await api.post("/gallery", fd, { headers });
      toast.success("Image uploaded");
      setCaption((c) => ({ ...c, [kind]: "" }));
      if (input) input.value = "";
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy((b) => ({ ...b, [kind]: false })); }
  };

  const remove = async (img) => {
    try {
      await api.delete(`/gallery/${img.image_id}`, { headers });
      toast.success("Image removed");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid={`${testid}-manager`}>
      {kinds.map((kind) => {
        const list = images.filter((i) => i.kind === kind);
        return (
          <div key={kind} className="space-y-3" data-testid={`${testid}-section-${kind}`}>
            <div>
              <p className="text-sm font-semibold text-slate-900">{KIND_LABEL[kind]} <span className="font-normal text-slate-400">({list.length}/10)</span></p>
              <p className="text-xs text-slate-500">{KIND_HINT[kind]}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                ref={(el) => (fileRefs.current[kind] = el)}
                className="bg-white sm:max-w-xs" data-testid={`${testid}-file-${kind}`} />
              <Input value={caption[kind] || ""} onChange={(e) => setCaption((c) => ({ ...c, [kind]: e.target.value }))}
                placeholder="Caption (optional)" maxLength={120} className="bg-white sm:max-w-xs" data-testid={`${testid}-caption-${kind}`} />
              <Button onClick={() => upload(kind)} disabled={busy[kind]} className="bg-slate-900 hover:bg-slate-800 shrink-0" data-testid={`${testid}-upload-${kind}`}>
                {busy[kind] ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ImagePlus className="h-4 w-4 mr-2" />} Upload
              </Button>
            </div>
            {list.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {list.map((img) => (
                  <div key={img.image_id} className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid={`${testid}-img-${img.image_id}`}>
                    <img src={mediaUrl(img.image_id)} alt={img.caption || img.original_filename} className={`w-full object-cover ${kind === "banner" ? "h-16" : "h-28"}`} />
                    {img.caption && <p className="text-[11px] text-slate-600 px-2 py-1 truncate">{img.caption}</p>}
                    <button onClick={() => remove(img)} data-testid={`${testid}-delete-${img.image_id}`}
                      className="absolute top-1.5 right-1.5 rounded-full bg-white/90 border border-slate-200 p-1.5 text-slate-500 hover:text-red-600 shadow-sm">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">No {KIND_LABEL[kind].toLowerCase()} yet.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
